import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import numberWithCommas from '../../utils/numberWithCommas';
import WaitingIndicator from '../../../../general-ui/waiting-indicator/waiting-indicator';

interface PieChartProps {
	data: any;
	index: number;
	title: string;
	loading?: boolean;
	error?: string | null
}

interface LegendProps {
	data: any;
	index: any;
	colors: any;
	format: any;
}

const Legend = ({ data, index, colors, format }: LegendProps) => {
	const [label, setLabel] = useState(data.data.label);
	useEffect(() => {
		setLabel(data.data.label);
	}, [data.data.label]);

	return <div className="legend-row">
		<svg className="legend-row-bullet" width="14px" height="14px" >
			<circle cx={6} cy={6} r={3} fill={colors(index)} />
		</svg>
		<p className="legend-row-label">
			{label}
		</p>

		<p className="legend-row-value">
			{numberWithCommas(format(data.value))}
		</p>
	</div>;
};

function Pie(props: PieChartProps): JSX.Element {

	if (props.error) {
		return <div className="retention-chart-loading">{props.error}</div>;
	}

	if (props.loading) {
		return <div className="retention-chart-loading"><WaitingIndicator /></div>;
	}

	if (!props.data.values?.length) {
		return <div className="retention-chart-loading">No data to display</div>;
	}


	const ref = useRef(null);
	const cache = useRef(props.data);
	const width = 80;
	const height = 80;
	const innerRadius = 20;
	const outerRadius = 40;

	const createPie = d3
		.pie()
		.value((d: any) => d.value)
		.sort(null);
	const createArc = d3
		.arc()
		.innerRadius(innerRadius)
		.outerRadius(outerRadius);
	const colors = d3.scaleOrdinal().range(["#83DBFF", "#CC7BF5", "#6663F9", "#3B9DFF", "#2EC083", "#FFB23B"]);
	const format = d3.format(".0f");
	const data = createPie(props.data.values);
	const hoverColor = "#eec42d";
	const staticColor = '#437c90';
	const tooltip = d3
		.select(`#donut-chart-${props.index}`)
		.append('div')
		.attr('class', 'd3-tooltip')
		.style('position', 'absolute')
		.style('z-index', '10')
		.style('visibility', 'hidden')
		.style('padding', '10px')
		.style('background', 'rgba(0,0,0,0.6)')
		.style('border-radius', '4px')
		.style('color', '#fff')
		.text('a simple tooltip');

	useEffect(
		() => {
			const data = createPie(props.data.values);
			const prevData = createPie(cache.current);
			const group = d3.select(ref.current);
			const groupWithData = group.selectAll("g.arc").data(data);
			groupWithData.exit().remove();
			const groupWithUpdate = groupWithData
				.enter()
				.append("g")
				.attr("class", "arc")
				.on('mouseover', function(d, i: any) {

					tooltip
						.html(
							`<div>${props.title}: ${i.data.label}</div><div>Value: ${numberWithCommas(i.data.value)}</div>`
						)
						.style('visibility', 'visible');
					d3.select(".d3-tooltip").transition().attr('fill', hoverColor);
				})
				.on('mousemove', function(d) {
					tooltip
						.style('top', d.pageY - 10 + 'px')
						.style('left', d.pageX + 10 + 'px');
				})
				.on('mouseout', function() {
					tooltip.html(``).style('visibility', 'hidden');
					d3.select(this).transition().attr('fill', staticColor);
				});


			const path: any = groupWithUpdate
				.append("path")
				.merge(groupWithData.select("path.arc"));

			const arcTween = (d: any, i: any) => {
				const interpolator = d3.interpolate(prevData[i], d);
				return (t: any) => createArc(interpolator(t));
			};
			path
				.attr("class", "arc")
				.attr("fill", (d: any, i: any) => colors(i))
				.transition()
				.attrTween("d", arcTween);
			cache.current = props.data.values;
		},
		[colors, createArc, createPie, props.data.values, props.title, tooltip]
	);

	return (
		<div className="donut-chart" id={`donut-chart-${props.index}`}>
			<svg width={width} height={height} className="donut-chart-donut">
				<g
					ref={ref}
					transform={`translate(${outerRadius} ${outerRadius})`}
				/>
			</svg>
			<div className="donut-chart-legend">
				{data.map((d, i) => (
					<Legend
						key={i}
						data={d}
						index={i}
						colors={colors}
						format={format}
					/>
				))}
			</div>
		</div>
	);
}

export default Pie;
